/**
 * ONE TAP — the audio engine.
 *
 * Owns the Web Audio graph, the listener, positional voices, the gun hums, the adaptive
 * score, and the master limiter. Every GameEvent that the simulation emits comes through
 * `handle()` and leaves as a sound placed at the right point in the world.
 *
 * Graph:
 *
 *   one-shot voice ─┬─▶ PannerNode(HRTF) ─▶ sfxBus ─┐
 *                   └─▶ wet gain ─▶ reverb send ─▶ reverb return ─▶ sfxBus
 *   music ──────────────────────────────────────────┴─▶ master ─▶ compressor ─▶ safety ─▶ out
 *
 * Three hard guarantees:
 *  1. **It never clips.** A compressor configured as a limiter sits in front of a soft-knee
 *     waveshaper that cannot mathematically exceed unity. Three explosions at once cannot
 *     produce a crackle.
 *  2. **It never stalls the main thread.** One-shots are capped and stolen by priority; the
 *     sustained voices (hums, scrapes, whooshes, tethers) each have their own hard cap.
 *  3. **It obeys the autoplay policy.** No AudioContext exists until `resume()` is called
 *     from a user gesture, so the browser never logs an autoplay warning.
 */

import { clamp, clamp01, cross, dirFromYawPitch, distance, lerpN, normalize, rightFromYaw, v3 } from '@shared/math';
import type { Vec3 } from '@shared/math';
import {
  GunState,
  HealthState,
  HitZone,
  MoveState,
  RoundPhase,
  SurfaceKind,
} from '@shared/types';
import type { GameEvent, GunEntity, PlayerState, WorldState } from '@shared/types';
import {
  GRAPPLE_NONE, GRAPPLE_MAX_SPEED, PLAYER_EYE_HEIGHT, ROUND_TIME_MS, RUN_SPEED, SLIDE_MAX_SPEED } from '@shared/constants';
import { ARENA } from '@shared/sim/arena';

import { ReverbBank } from './reverb';
import type { IRKind } from './reverb';
import { Sfx } from './sfx';
import type { AmbienceVoice, HumVoice, ScrapeVoice, SfxTarget, TetherVoice, WhooshVoice } from './sfx';
import { Music } from './music';
import type { MusicState } from './music';
import { MIN_GAIN, NoiseFactory, gain, makeLimiterCurve, shaper } from './synth';

// ---------------------------------------------------------------------------
// Mix tuning. These are mixing-desk numbers, not gameplay numbers — they change how the
// game sounds, never how it plays, so they live here rather than in @shared/constants.
// ---------------------------------------------------------------------------

/** Hard cap on simultaneous one-shot voices. Beyond this we steal by priority. */
const MAX_VOICES = 40;
const MAX_HUMS = 4;
const MAX_WHOOSHES = 3;
const MAX_SCRAPES = 5;
const MAX_TETHERS = 4;

/** Priority table. Higher wins a contested voice slot. */
const PRI = {
  ui: 100,
  hitMarker: 98,
  dryFire: 96,
  gunshot: 95,
  stinger: 94,
  kill: 90,
  catch: 85,
  heal: 80,
  pulse: 70,
  grapple: 60,
  dash: 55,
  impact: 50,
  bounce: 45,
  land: 40,
  jump: 35,
  footstep: 20,
  /** Density layers. First to be stolen — they must never cost an audible event its slot. */
  foley: 12,
} as const;

/** Panner presets. `ref` is the distance at which a sound is at full level. */
const SPACE = {
  /** Loud, arena-wide events. Rolls off slowly so a shot across the map still reads. */
  loud: { ref: 12, rolloff: 0.5, max: 260 },
  /** Normal world events. */
  world: { ref: 5, rolloff: 1.0, max: 120 },
  /** Small, local detail — footsteps, scrapes. Falls away fast so it never clutters. */
  detail: { ref: 2.5, rolloff: 1.35, max: 45 },
  /** Intel sounds that must carry: the gun hum, the dry click. */
  intel: { ref: 8, rolloff: 0.8, max: 90 },
} as const;

/**
 * Relative loudness of every sound in the game, in one place.
 *
 * This table is the mix. **The gunshot is 1.0 and nothing else is allowed to reach it** —
 * a dash that peaks as hard as a gunshot makes the gunshot stop being an event. The
 * previous table had `catch` at 1.3, thirty percent ABOVE the shot, which is exactly the
 * kind of thing that makes a mix read as "nothing feels consequential": if the reward sound
 * for a routine pass is louder than the kill shot, neither of them means anything.
 *
 * The whole quiet half of the table has come up ~5 dB and the loud half has come up less,
 * because the complaint was thinness, not lack of volume. The limiter now only ever works
 * on the shot, the kill and the stingers, which is what makes those three feel enormous.
 */
const LVL = {
  gunshot: 1.0,

  // --- The core loop. These are the events a player follows a round by, and after the
  // --- playtest verdict ("we need definitive sounds... very dramatic and definitive") they
  // --- sit in a band of their own, clearly above the traversal layer and clearly below the
  // --- shot. The THROW in particular came up from 0.42 — it was mixed like a footstep and
  // --- it is the single most dramatic thing a player does with the gun.
  throw: 0.86,
  catch: 0.8,
  kill: 0.76,
  dryFire: 0.7,
  disarm: 0.68,
  /** The sustained flight voice. Not a one-shot, but it belongs in this table. */
  whoosh: 0.78,

  stinger: 0.62,
  hitMarker: 0.58,
  impact: 0.56,
  wound: 0.54,
  pulse: 0.52,
  heal: 0.48,
  heartbeat: 0.46,

  // --- Traversal and detail. Deliberately a wide step below the loop above: nothing feels
  // --- huge unless something is small (DESIGN.md §5.2), and the previous table's entire
  // --- range from gunshot to footstep was only 9 dB.
  grapple: 0.4,
  bounce: 0.38,
  land: 0.36,
  walljump: 0.36,
  dash: 0.34,
  pickup: 0.34,
  slide: 0.3,
  footstep: 0.27,
  wallrun: 0.27,
  jump: 0.24,
  foley: 0.2,
} as const;

/** How far a player can be before we stop bothering to give them a sustained voice. */
const SUSTAIN_CULL_DIST = 46;
/** Metres of travel between footsteps at full running speed. */
const STRIDE_METRES = 2.15;
/** Speed of sound, for the manual doppler on thrown guns. */
const SPEED_OF_SOUND = 343;

/**
 * Doppler exaggeration on a thrown gun.
 *
 * Physically honest doppler for a 26 m/s object is a ratio of about 1.08 — roughly one
 * semitone. That is correct and it is also completely inaudible over a broadband whoosh in
 * a noisy mix, which is why the previous pass had "doppler" that nobody could hear. Films
 * have exaggerated pass-bys since the 1930s for exactly this reason. 3.2x puts a hard throw
 * at about a major third of shift across the pass, which reads unmistakably as something
 * going by and still sounds like physics rather than a pitch bend.
 */
const DOPPLER_EXAGGERATION = 3.2;
/** Radial velocity is differentiated per frame, so it needs smoothing or it chatters. */
const DOPPLER_SMOOTHING = 0.35;
/** Within this many metres a passing gun is "over your head" and opens right up. */
const FLYBY_RADIUS = 15;
/** Below this speed a gun is tumbling to rest, not flying, and the whoosh retires. */
const FLIGHT_MIN_SPEED = 2.5;
/** Gun speed that maps to a fully-open whoosh. THROW_SPEED_CHARGED is 38. */
const FLIGHT_REF_SPEED = 30;

interface Voice {
  id: number;
  priority: number;
  endsAt: number;
  out: GainNode;
  wet: GainNode | null;
  panner: PannerNode | null;
  timer: ReturnType<typeof setTimeout> | null;
  killed: boolean;
}

interface SpatialOpts {
  priority: number;
  /** World position, or null for a head-locked (UI/local) sound. */
  pos: Vec3 | null;
  /** Base reverb send, before distance shaping. 0 = bone dry. */
  wet: number;
  space?: IRKind;
  preset?: { ref: number; rolloff: number; max: number };
  local?: boolean;
  level?: number;
}

interface HumSlot {
  voice: HumVoice;
  panner: PannerNode;
  out: GainNode;
}

interface WhooshSlot {
  voice: WhooshVoice;
  panner: PannerNode;
  out: GainNode;
  wet: GainNode | null;
  /** Smoothed radial velocity, m/s. Positive is receding. */
  radial: number;
}

interface ScrapeSlot {
  kind: 'slide' | 'wallrun';
  voice: ScrapeVoice;
  panner: PannerNode;
  out: GainNode;
}

interface TetherSlot {
  voice: TetherVoice;
  panner: PannerNode;
  out: GainNode;
}

interface PlayerTrack {
  lastX: number;
  lastZ: number;
  strideAccum: number;
  surface: SurfaceKind;
}

/**
 * Reference levels for the four sub-buses — the mix the game was balanced at, with every
 * fader wide open. The settings panel's music/SFX sliders scale these; they never replace
 * them, so a slider at 1.0 is exactly the designed balance.
 *
 * SFX came up and music went down. Conservative buses are their own failure mode: if the
 * whole game is mixed 6 dB shy the player turns their system up, the noise floor comes with
 * it, and everything reads as timid no matter how good the individual sounds are.
 */
const SFX_BUS_LEVEL = 0.6;
const MUSIC_BUS_LEVEL = 0.26;
const UI_BUS_LEVEL = 0.58;
/** The world bed sits well under everything. It is felt, not heard. */
const AMBIENCE_BUS_LEVEL = 0.3;

/** Post-limiter makeup. The safety curve below cannot exceed unity, so this cannot clip. */
const MASTER_MAKEUP = 1.32;

/**
 * Output monitor emulation for `setMonitorMode`.
 *
 * `laptop` is the important one: DESIGN.md §5.4 requires every sound to still work when
 * everything below ~150 Hz is gone, because that is the machine most players are on. This
 * makes that check something a human can hear rather than something a mixer has to imagine.
 */
export type MonitorMode = 'full' | 'laptop';
const MONITOR_FLAT_HZ = 12;
const MONITOR_LAPTOP_HZ = 170;

/**
 * Sidechain: how hard, and for how long, the bed and score duck under a big event.
 *
 * "Silence around an event is what makes the event enormous" (DESIGN.md §5.2). These are all
 * deeper and longer than the previous pass's — a 62% duck for 240 ms is a mixing decision, a
 * 85% duck for 340 ms is a dramatic one, and this game needs the dramatic one.
 */
const DUCK_SHOT = { amount: 0.85, seconds: 0.34 };
const DUCK_KILL = { amount: 0.62, seconds: 0.36 };
const DUCK_CLICK = { amount: 0.7, seconds: 0.24 };
/**
 * The throw gets its own duck. The gun is airborne for a second or more and the whoosh has
 * to be followable for all of it, so the bed steps back for the launch and comes home while
 * the gun is still in the air rather than snapping back into the flight.
 */
const DUCK_THROW = { amount: 0.55, seconds: 0.5 };

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private uiBus: GainNode | null = null;
  private ambienceBus: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private makeup: GainNode | null = null;
  private safety: WaveShaperNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private monitorA: BiquadFilterNode | null = null;
  private monitorB: BiquadFilterNode | null = null;
  private monitorMode: MonitorMode = 'full';

  private ambience: AmbienceVoice | null = null;
  /** Steps taken by each gun-carrier since their last handling-foley, for cadence. */
  private readonly foleyCounters = new Map<string, number>();

  private reverb: ReverbBank | null = null;
  private noise: NoiseFactory | null = null;
  private sfx: Sfx | null = null;
  private music: Music | null = null;

  private masterVolume = 1;
  /** Sub-bus faders, 0..1. Multiplied into the reference bus levels below. */
  private musicVolume = 1;
  private sfxVolume = 1;
  private ambienceLevel = 1;
  private ready = false;
  private disposed = false;
  private resuming: Promise<void> | null = null;

  private readonly voices: Voice[] = [];
  private nextVoiceId = 1;

  private readonly hums = new Map<number, HumSlot>();
  private readonly whooshes = new Map<number, WhooshSlot>();
  private readonly scrapes = new Map<string, ScrapeSlot>();
  private readonly tethers = new Map<string, TetherSlot>();
  private readonly tracks = new Map<string, PlayerTrack>();

  private listenerPos: Vec3 = v3(0, 0, 0);

  private heartbeatTimer = 0;
  private woundedElapsed = 0;
  private lastPhase: RoundPhase = RoundPhase.Lobby;

  // Scratch vectors — this runs every frame and must not allocate.
  private readonly _fwd = v3();
  private readonly _right = v3();
  private readonly _up = v3();
  private readonly _pos = v3();

  /** Set once the browser has ever refused to start audio, so the UI can say so. */
  private blocked = false;
  /** Notified when `ready` or `blocked` changes, so the HUD can surface a muted game. */
  onStateChange: ((state: { ready: boolean; blocked: boolean }) => void) | null = null;
  private armed = false;
  private readonly arm = () => {
    void this.resume().catch(() => undefined);
  };

  constructor() {
    // No AudioContext here: constructing one before a user gesture makes Chrome log an
    // autoplay warning, and DESIGN.md §7 requires a clean console. Built lazily in resume().
    //
    // But we DO self-arm on the first real user gesture anywhere on the page. Relying only on
    // the game's own input callbacks made silence possible and, worse, invisible: an
    // automated session (synthetic clicks via CDP do not count as user activation) plays
    // completely mute, and a human who clicks somewhere the game is not listening gets the
    // same. Audio failing silently is indistinguishable from audio being badly mixed, which
    // sent a playtest chasing the wrong bug. These listeners are `once` and cost nothing.
    this.armListeners();
  }

  /**
   * Forced silence, from `?mute=1` or `?mute`. Survives every other volume path.
   *
   * Automated sessions drive the page with CDP-dispatched input, which counts as a trusted
   * user gesture and therefore ARMS the audio engine — so an agent playtesting in a background
   * tab blasts the machine's owner at full volume while they are trying to work. That is
   * unacceptable regardless of how careful the agent is, so it is enforced here rather than
   * left to agent discipline.
   */
  private readonly forceMuted: boolean = (() => {
    // Automation is muted unconditionally. `navigator.webdriver` is true in any CDP- or
    // WebDriver-driven browser, which is exactly the case where a tab is playing to nobody
    // while its owner is trying to work. Agent discipline is not a reliable control here —
    // a prompt can be forgotten or an agent replaced — so the detection lives in the engine.
    // A human can still opt back in with `?mute=0`.
    try {
      const params =
        typeof location === 'undefined' ? null : new URLSearchParams(location.search);
      if (params?.get('mute') === '0') return false;
      if (params?.has('mute')) return true;
      if (typeof navigator !== 'undefined' && navigator.webdriver === true) return true;
    } catch {
      /* fall through to audible */
    }
    return false;
  })();

  /** True while the tab is hidden or unfocused — the game must not make noise in the background. */
  private backgrounded = false;

  private readonly onVisibility = () => {
    const hidden = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());
    if (hidden === this.backgrounded) return;
    this.backgrounded = hidden;
    this.applyMasterGain();
  };

  /** Master gain actually applied, after the force-mute and background gates. */
  private applyMasterGain(): void {
    if (!this.ctx || !this.master) return;
    const target = this.forceMuted || this.backgrounded ? 0 : this.masterVolume;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(target, t, 0.04);
  }

  private armListeners(): void {
    if (this.armed || typeof window === 'undefined') return;
    this.armed = true;
    // `?mute` means the page never even builds an AudioContext, so it cannot make noise at all.
    if (this.forceMuted) return;
    const opts = { once: true, capture: true, passive: true } as const;
    window.addEventListener('pointerdown', this.arm, opts);
    window.addEventListener('keydown', this.arm, opts);
    window.addEventListener('touchstart', this.arm, opts);
    // Background gating is permanent, not once.
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('blur', this.onVisibility);
    window.addEventListener('focus', this.onVisibility);
  }

  private disarmListeners(): void {
    if (!this.armed || typeof window === 'undefined') return;
    this.armed = false;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('blur', this.onVisibility);
    window.removeEventListener('focus', this.onVisibility);
    const opts = { capture: true } as const;
    window.removeEventListener('pointerdown', this.arm, opts);
    window.removeEventListener('keydown', this.arm, opts);
    window.removeEventListener('touchstart', this.arm, opts);
  }

  /** True when the browser is refusing to start audio and the game is therefore silent. */
  isBlocked(): boolean {
    return this.blocked && !this.ready;
  }

  isReady(): boolean {
    return this.ready;
  }

  private publishState(): void {
    this.onStateChange?.({ ready: this.ready, blocked: this.isBlocked() });
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /** Call from a user gesture. Safe to call repeatedly. */
  resume(): Promise<void> {
    if (this.disposed || this.forceMuted) return Promise.resolve();
    if (this.resuming) return this.resuming;

    this.resuming = (async () => {
      try {
        if (!this.ctx) this.build();
        const ctx = this.ctx;
        if (!ctx) return;
        if (ctx.state === 'suspended') await ctx.resume();
        this.ready = ctx.state === 'running';
        if (this.ready) {
          this.blocked = false;
          this.disarmListeners();
          if (this.music) {
            this.music.setState('menu');
            this.music.start();
          }
        } else {
          // Still suspended: the browser has not seen a qualifying gesture yet. Stay armed.
          this.blocked = true;
        }
      } catch {
        // Audio is a luxury, not a dependency. If the browser refuses, the game plays silent —
        // but it must never do so INVISIBLY. `blocked` drives a HUD indicator.
        this.ready = false;
        this.blocked = true;
      } finally {
        this.resuming = null;
        this.publishState();
      }
    })();

    return this.resuming;
  }

  private build(): void {
    const Ctor: typeof AudioContext | undefined =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;

    // --- Master chain.
    //
    //   master ─▶ limiter ─▶ makeup ─▶ safety curve ─▶ analyser ─▶ out
    //
    // The limiter catches the peaks, the makeup gain puts the level back where it belongs,
    // and the safety curve is a transfer function that cannot mathematically exceed 1.0 —
    // so the makeup can never turn into a clip no matter how many guns fire at once. The
    // curve's soft knee at 0.72 means the very loudest events get a little glue saturation
    // on the way past, which is exactly the "crack that clips the limiter" DESIGN.md asks
    // the gunshot for.
    const safety = shaper(ctx, makeLimiterCurve(0.72), '4x');
    // Monitor emulation, flat by default. `setMonitorMode('laptop')` sweeps these up to
    // 170 Hz for a 24 dB/oct rolloff, which is roughly what a thin laptop actually does. It
    // is a tooling affordance, not a game feature — but the game is MIXED through it,
    // because most players will hear it that way and no sub at all.
    const monA = ctx.createBiquadFilter();
    monA.type = 'highpass';
    monA.frequency.value = MONITOR_FLAT_HZ;
    monA.Q.value = 0.6;
    const monB = ctx.createBiquadFilter();
    monB.type = 'highpass';
    monB.frequency.value = MONITOR_FLAT_HZ;
    monB.Q.value = 0.6;
    // The limiter is a SAFETY NET, not a mix bus compressor, and the difference is the
    // whole reason the gunshot did not land.
    //
    // The previous settings — threshold -3 dB, ratio 20, attack 1.5 ms, release 120 ms —
    // meant everything loud in the game was being squashed into a 3 dB window, and the
    // 1.5 ms attack was fast enough to catch the leading edge of the very transients the
    // shot depends on. The shot then held the compressor down for its whole 120 ms release,
    // so its own body arrived quieter than its tail. That is a mix that cannot have a
    // "loudest thing by a wide margin" in it, because nothing is allowed to be.
    //
    // Now: the threshold sits just under the ceiling, the ratio is gentle, and the 5 ms
    // attack is deliberately SLOWER than any transient in the kit — so the first few
    // milliseconds of a gunshot pass through the limiter untouched and land on the safety
    // curve's soft knee instead. That soft knee is a saturator, so what the ear gets is the
    // "crack that clips the limiter" DESIGN.md §5 asks for, rather than a crack that got
    // quietly turned down. The fast release stops one shot from ducking the next 200 ms of
    // the game.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1;
    limiter.knee.value = 0;
    limiter.ratio.value = 10;
    limiter.attack.value = 0.005;
    limiter.release.value = 0.055;
    const makeup = gain(ctx, MASTER_MAKEUP);

    // The audit tap. Free when nobody reads it, and it is what `tools/audio-lab.html`
    // renders its peak/RMS meter and spectrum from.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.72;

    const master = gain(ctx, this.masterVolume);
    master.connect(limiter);
    limiter.connect(makeup);
    makeup.connect(safety);
    safety.connect(monA);
    monA.connect(monB);
    // The analyser reads POST-monitor, so the lab's meter and spectrum show what the
    // emulated speaker actually reproduces rather than what the mix theoretically contains.
    monB.connect(analyser);
    analyser.connect(ctx.destination);

    const sfxBus = gain(ctx, SFX_BUS_LEVEL * this.sfxVolume);
    const musicBus = gain(ctx, MUSIC_BUS_LEVEL * this.musicVolume);
    const uiBus = gain(ctx, UI_BUS_LEVEL * this.sfxVolume);
    const ambienceBus = gain(ctx, AMBIENCE_BUS_LEVEL * this.sfxVolume * this.ambienceLevel);
    sfxBus.connect(master);
    musicBus.connect(master);
    uiBus.connect(master);
    ambienceBus.connect(master);

    this.master = master;
    this.limiter = limiter;
    this.makeup = makeup;
    this.safety = safety;
    this.analyserNode = analyser;
    this.monitorA = monA;
    this.monitorB = monB;
    this.applyMonitorMode();
    this.sfxBus = sfxBus;
    this.musicBus = musicBus;
    this.uiBus = uiBus;
    this.ambienceBus = ambienceBus;

    // --- Rooms. Returns land on the SFX bus so the SFX fader governs them.
    this.reverb = new ReverbBank(ctx, sfxBus, ['atrium', 'interior', 'plate']);
    this.noise = new NoiseFactory(ctx);
    this.sfx = new Sfx(ctx, this.reverb, this.noise);
    this.music = new Music(ctx, musicBus, this.reverb.buffer('plate'), this.noise);

    // Default listener orientation until the camera reports in.
    this.setListener(this.listenerPos, 0, 0);
  }

  setMasterVolume(v: number): void {
    this.masterVolume = clamp(v, 0, 1.5);
    this.applyMasterGain();
  }

  /**
   * Music and SFX faders, 0..1, independent of master.
   *
   * The two sub-bus gains set in `build()` are the mix the game was balanced at, so a
   * fader scales that reference level rather than replacing it — a slider at its default
   * must leave the balance exactly where the sound designer put it. Both are remembered
   * across a `resume()`, so a player who turns the music down before the AudioContext
   * exists still gets what they asked for when it appears.
   */
  setMusicVolume(v: number): void {
    this.musicVolume = clamp(v, 0, 1);
    this.rampBus(this.musicBus, MUSIC_BUS_LEVEL * this.musicVolume);
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = clamp(v, 0, 1);
    this.rampBus(this.sfxBus, SFX_BUS_LEVEL * this.sfxVolume);
    // UI clicks and the world bed ride the SFX fader too — muting "sound effects" and still
    // hearing the menus click, or the wind, would read as a bug.
    this.rampBus(this.uiBus, UI_BUS_LEVEL * this.sfxVolume);
    this.rampBus(this.ambienceBus, AMBIENCE_BUS_LEVEL * this.sfxVolume * this.ambienceLevel);
  }

  /**
   * Trim on the world bed, 0..1, applied on top of the SFX fader.
   *
   * Some players genuinely cannot stand a continuous room tone, and a bed that cannot be
   * turned down is a bed that gets the whole game muted instead. 1 is the designed level.
   */
  setAmbienceLevel(v: number): void {
    this.ambienceLevel = clamp(v, 0, 1);
    this.rampBus(this.ambienceBus, AMBIENCE_BUS_LEVEL * this.sfxVolume * this.ambienceLevel);
  }

  /**
   * Master-output analyser for the audio lab. Never used by the game itself — it exists so
   * a human can look at the mix as well as listen to it, which is the only way peak, RMS
   * and spectral balance get judged at all.
   */
  analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /** The live AudioContext, or null before the first `resume()`. For tooling only. */
  context(): AudioContext | null {
    return this.ctx;
  }

  /**
   * Switch the master output between a full-range monitor and a laptop-speaker emulation.
   * Persisted across a rebuild of the graph, so setting it before `resume()` works.
   */
  setMonitorMode(mode: MonitorMode): void {
    this.monitorMode = mode;
    this.applyMonitorMode();
  }

  getMonitorMode(): MonitorMode {
    return this.monitorMode;
  }

  private applyMonitorMode(): void {
    const ctx = this.ctx;
    if (!ctx || !this.monitorA || !this.monitorB) return;
    const hz = this.monitorMode === 'laptop' ? MONITOR_LAPTOP_HZ : MONITOR_FLAT_HZ;
    const t = ctx.currentTime;
    for (const f of [this.monitorA, this.monitorB]) {
      f.frequency.cancelScheduledValues(t);
      f.frequency.setTargetAtTime(hz, t, 0.02);
    }
  }

  /**
   * Sidechain. Everything that is a bed rather than an event steps aside for ~200 ms.
   *
   * This is the other half of "nothing feels consequential": loudness is relative, and a
   * gunshot is only enormous if the world goes quiet around it. Only the music and the
   * ambience duck — ducking the SFX bus would duck the gunshot itself.
   */
  private duckBeds(amount: number, seconds: number): void {
    this.music?.duck(amount, seconds);
    this.ambience?.duck(amount * 0.85, seconds);
  }

  private rampBus(bus: GainNode | null, level: number): void {
    if (!this.ctx || !bus) return;
    const t = this.ctx.currentTime;
    bus.gain.cancelScheduledValues(t);
    bus.gain.setTargetAtTime(level, t, 0.05);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.ready = false;
    this.onStateChange = null;
    this.disarmListeners();

    for (const v of this.voices) this.killVoice(v, 0.01);
    this.voices.length = 0;
    for (const h of this.hums.values()) h.voice.stop(0.05);
    this.hums.clear();
    for (const w of this.whooshes.values()) w.voice.stop(0.05);
    this.whooshes.clear();
    for (const s of this.scrapes.values()) s.voice.stop(0.05);
    this.scrapes.clear();
    for (const t of this.tethers.values()) t.voice.stop(0.05);
    this.tethers.clear();
    this.tracks.clear();
    this.foleyCounters.clear();
    this.ambience?.stop(0.12);
    this.ambience = null;

    this.music?.dispose();
    this.sfx?.dispose();
    this.reverb?.dispose();

    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.sfxBus = null;
    this.musicBus = null;
    this.uiBus = null;
    this.ambienceBus = null;
    this.reverb = null;
    this.sfx = null;
    this.music = null;
    this.noise = null;
    this.limiter = null;
    this.makeup = null;
    this.safety = null;
    this.analyserNode = null;
    this.monitorA = null;
    this.monitorB = null;

    if (ctx) {
      // Give scheduled fades a moment to run out before tearing the context down.
      setTimeout(() => {
        void ctx.close().catch(() => undefined);
      }, 260);
    }
  }

  // -------------------------------------------------------------------------
  // Listener
  // -------------------------------------------------------------------------

  setListener(pos: Vec3, yaw: number, pitch: number): void {
    this.listenerPos.x = pos.x;
    this.listenerPos.y = pos.y;
    this.listenerPos.z = pos.z;

    dirFromYawPitch(this._fwd, yaw, pitch);

    const ctx = this.ctx;
    if (!ctx) return;

    // Up vector from the actual view basis: at extreme pitch a fixed world-up becomes
    // parallel to forward and the HRTF panning collapses.
    rightFromYaw(this._right, yaw);
    cross(this._up, this._right, this._fwd);
    normalize(this._up, this._up);

    const l = ctx.listener;
    const t = ctx.currentTime;
    if (l.positionX) {
      l.positionX.setTargetAtTime(pos.x, t, 0.01);
      l.positionY.setTargetAtTime(pos.y, t, 0.01);
      l.positionZ.setTargetAtTime(pos.z, t, 0.01);
      l.forwardX.setTargetAtTime(this._fwd.x, t, 0.01);
      l.forwardY.setTargetAtTime(this._fwd.y, t, 0.01);
      l.forwardZ.setTargetAtTime(this._fwd.z, t, 0.01);
      l.upX.setTargetAtTime(this._up.x, t, 0.01);
      l.upY.setTargetAtTime(this._up.y, t, 0.01);
      l.upZ.setTargetAtTime(this._up.z, t, 0.01);
    } else {
      const legacy = l as unknown as {
        setPosition(x: number, y: number, z: number): void;
        setOrientation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number): void;
      };
      legacy.setPosition(pos.x, pos.y, pos.z);
      legacy.setOrientation(this._fwd.x, this._fwd.y, this._fwd.z, this._up.x, this._up.y, this._up.z);
    }
  }

  // -------------------------------------------------------------------------
  // Voice management
  // -------------------------------------------------------------------------

  private makePanner(pos: Vec3, preset: { ref: number; rolloff: number; max: number }): PannerNode {
    const ctx = this.ctx!;
    const p = ctx.createPanner();
    p.panningModel = 'HRTF';
    p.distanceModel = 'inverse';
    p.refDistance = preset.ref;
    p.maxDistance = preset.max;
    p.rolloffFactor = preset.rolloff;
    p.coneInnerAngle = 360;
    p.coneOuterAngle = 360;
    p.coneOuterGain = 1;
    this.movePanner(p, pos, false);
    return p;
  }

  /**
   * `tau` is the smoothing time constant. The 20 ms default is right for a walking player —
   * it takes the stair-step out of interpolated snapshot positions. It is wrong for a thrown
   * gun: `setTargetAtTime` is only ~95% of the way there after 3 tau, so at 20 ms a gun
   * moving 30 m/s has its HRTF image trailing nearly two metres behind it, and the error is
   * worst exactly during a fly-by, when the angular rate is highest and the listener is
   * trying to work out where to look. Flight passes a much shorter one.
   */
  private movePanner(p: PannerNode, pos: Vec3, smooth: boolean, tau = 0.02): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (p.positionX) {
      const t = ctx.currentTime;
      if (smooth) {
        p.positionX.setTargetAtTime(pos.x, t, tau);
        p.positionY.setTargetAtTime(pos.y, t, tau);
        p.positionZ.setTargetAtTime(pos.z, t, tau);
      } else {
        p.positionX.setValueAtTime(pos.x, t);
        p.positionY.setValueAtTime(pos.y, t);
        p.positionZ.setValueAtTime(pos.z, t);
      }
    } else {
      (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(pos.x, pos.y, pos.z);
    }
  }

  /**
   * Distance shaping for the reverb send. Wet rises with distance (that is what tells you
   * something is far away) and then falls off once the source is beyond the room.
   */
  private wetFor(dist: number, base: number): number {
    if (base <= 0) return 0;
    // The wet/dry ratio climbs with distance — that ratio IS the cue that says "far away".
    const ratio = 0.34 + 0.66 * clamp01(dist / 26);
    // But the send still attenuates, just far more gently than the dry path, or a shot from
    // across the arena would arrive as loud as one fired next to your ear.
    const rolloff = 14 / (14 + 0.4 * Math.max(0, dist - 14));
    return base * ratio * rolloff;
  }

  /** Reserve a voice slot, stealing the lowest-priority active voice if we are full. */
  private reserve(priority: number): boolean {
    const ctx = this.ctx;
    if (!ctx) return false;
    const now = ctx.currentTime;

    for (let i = this.voices.length - 1; i >= 0; i--) {
      const v = this.voices[i]!;
      if (v.killed || v.endsAt <= now) {
        this.retire(v);
        this.voices.splice(i, 1);
      }
    }
    if (this.voices.length < MAX_VOICES) return true;

    let worst = -1;
    let worstPri = Number.POSITIVE_INFINITY;
    let worstEnd = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.voices.length; i++) {
      const v = this.voices[i]!;
      if (v.priority < worstPri || (v.priority === worstPri && v.endsAt < worstEnd)) {
        worst = i;
        worstPri = v.priority;
        worstEnd = v.endsAt;
      }
    }
    if (worst < 0 || worstPri >= priority) return false;

    const victim = this.voices[worst]!;
    this.killVoice(victim, 0.025);
    this.voices.splice(worst, 1);
    return true;
  }

  private killVoice(v: Voice, fadeSec: number): void {
    if (v.killed) return;
    v.killed = true;
    const ctx = this.ctx;
    if (v.timer !== null) {
      clearTimeout(v.timer);
      v.timer = null;
    }
    if (ctx) {
      const t = ctx.currentTime;
      for (const g of [v.out, v.wet]) {
        if (!g) continue;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(Math.max(g.gain.value, MIN_GAIN), t);
        g.gain.exponentialRampToValueAtTime(MIN_GAIN, t + Math.max(fadeSec, 0.005));
        g.gain.setValueAtTime(0, t + Math.max(fadeSec, 0.005) + 0.001);
      }
    }
    setTimeout(() => this.retire(v), Math.max(fadeSec, 0.005) * 1000 + 40);
  }

  private retire(v: Voice): void {
    if (v.timer !== null) {
      clearTimeout(v.timer);
      v.timer = null;
    }
    try {
      v.out.disconnect();
      v.wet?.disconnect();
      v.panner?.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  /**
   * Build a routed target, hand it to a sound generator, and register the resulting voice
   * for cleanup. `emit` returns the sound's duration in seconds.
   */
  private spawn(opts: SpatialOpts, emit: (t: SfxTarget) => number): void {
    if (!this.ready || !this.ctx || !this.sfxBus || !this.reverb) return;

    const ctx = this.ctx;
    const dist = opts.pos ? distance(opts.pos, this.listenerPos) : 0;
    const preset = opts.preset ?? SPACE.world;
    // Beyond max distance the panner would return silence anyway; skip the node churn.
    // Checked BEFORE reserving, so an inaudible sound never steals an audible one's slot.
    if (opts.pos && dist > preset.max * 1.1 && opts.wet <= 0) return;
    if (!this.reserve(opts.priority)) return;

    // The level applies to BOTH paths. Scaling only the dry send would let a wet-heavy sound
    // (a kill, a PULSE, a stinger) sail past the mix table on its reverb alone.
    const lvl = opts.level ?? 1;
    const out = gain(ctx, lvl);
    let panner: PannerNode | null = null;
    if (opts.pos) {
      panner = this.makePanner(opts.pos, preset);
      out.connect(panner);
      panner.connect(this.sfxBus);
    } else {
      out.connect(this.uiBus ?? this.sfxBus);
    }

    const space: IRKind = opts.space ?? 'atrium';
    const wetAmount = this.wetFor(dist, opts.wet);
    let wet: GainNode | null = null;
    if (wetAmount > 0.001) {
      wet = gain(ctx, wetAmount * lvl);
      wet.connect(this.reverb.send(space));
    }

    const target: SfxTarget = {
      dry: out,
      wet,
      space,
      distance: dist,
      local: opts.local ?? false,
    };

    let seconds = 0.5;
    try {
      seconds = emit(target);
    } catch {
      // A single malformed sound must never take the frame down.
      seconds = 0.2;
    }

    // Distant sounds arrive late. It is a tiny detail and it is exactly the kind of thing
    // that makes an arena feel like a space rather than a diorama.
    const travel = dist > 12 ? Math.min(dist / SPEED_OF_SOUND, 0.45) : 0;
    const endsAt = ctx.currentTime + seconds + travel + 0.15;

    const voice: Voice = {
      id: this.nextVoiceId++,
      priority: opts.priority,
      endsAt,
      out,
      wet,
      panner,
      timer: null,
      killed: false,
    };
    voice.timer = setTimeout(
      () => {
        const idx = this.voices.indexOf(voice);
        if (idx >= 0) this.voices.splice(idx, 1);
        this.retire(voice);
      },
      (seconds + travel + 0.25) * 1000,
    );
    this.voices.push(voice);
  }

  // -------------------------------------------------------------------------
  // Positions
  // -------------------------------------------------------------------------

  private playerPos(w: WorldState, id: string | null, height: number): Vec3 | null {
    if (!id) return null;
    const p = w.players[id];
    if (!p) return null;
    this._pos.x = p.pos.x;
    this._pos.y = p.pos.y + height;
    this._pos.z = p.pos.z;
    return this._pos;
  }

  private gunWorldPos(w: WorldState, g: GunEntity, out: Vec3): Vec3 {
    if (g.holder) {
      const h = w.players[g.holder];
      if (h) {
        out.x = h.pos.x;
        out.y = h.pos.y + PLAYER_EYE_HEIGHT * 0.85;
        out.z = h.pos.z;
        return out;
      }
    }
    out.x = g.pos.x;
    out.y = g.pos.y;
    out.z = g.pos.z;
    return out;
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  handle(e: GameEvent, w: WorldState, localId: string): void {
    if (!this.ready || !this.sfx) return;
    const sfx = this.sfx;

    switch (e.t) {
      case 'shot': {
        const local = e.player === localId;
        this.spawn(
          { priority: PRI.gunshot, pos: e.origin, wet: 1.0, space: 'atrium', preset: SPACE.loud, local, level: LVL.gunshot },
          (t) => sfx.gunshot(t),
        );
        this.duckBeds(local ? DUCK_SHOT.amount : DUCK_SHOT.amount * 0.7, DUCK_SHOT.seconds);

        // The impact, wherever the round landed.
        if (e.hitPlayer) {
          this.spawn(
            { priority: PRI.impact, pos: e.hitPoint, wet: 0.35, space: 'atrium', preset: SPACE.world, level: LVL.impact },
            (t) => sfx.fleshImpact(t, e.zone === HitZone.Head),
          );
        } else {
          this.spawn(
            { priority: PRI.impact, pos: e.hitPoint, wet: 0.5, space: 'atrium', preset: SPACE.world, level: LVL.impact },
            (t) => sfx.bulletImpact(t, this.surfaceAt(e.hitPoint)),
          );
        }

        // Hit marker — head-locked UI, only for the shooter.
        if (local && e.hitPlayer) {
          this.spawn({ priority: PRI.hitMarker, pos: null, wet: 0, local: true, level: LVL.hitMarker }, (t) =>
            sfx.hitMarker(t, e.zone === HitZone.Head),
          );
        }
        break;
      }

      case 'dryfire': {
        const local = e.player === localId;
        const pos = local ? null : this.playerPos(w, e.player, PLAYER_EYE_HEIGHT * 0.9);
        this.spawn({ priority: PRI.dryFire, pos, wet: 0, preset: SPACE.intel, local, level: LVL.dryFire }, (t) =>
          sfx.dryFire(t),
        );
        // The floor dropping out. A dry click is a tiny sound, so the only way to make it
        // land is to take everything else away for a moment — the silence is the sound.
        if (local) this.duckBeds(DUCK_CLICK.amount, DUCK_CLICK.seconds);
        break;
      }

      case 'kill': {
        const pos = this.playerPos(w, e.victim, 0.9);
        const byLocal = e.killer === localId;
        const onLocal = e.victim === localId;
        this.spawn(
          { priority: PRI.kill, pos, wet: 0.7, space: 'atrium', preset: SPACE.loud, local: byLocal || onLocal, level: LVL.kill },
          (t) => sfx.kill(t, { byLocal, onLocal, head: e.zone === HitZone.Head }),
        );
        this.duckBeds(DUCK_KILL.amount, DUCK_KILL.seconds);
        if (onLocal) {
          this.heartbeatTimer = 0;
          this.woundedElapsed = 0;
        }
        break;
      }

      case 'wound': {
        const local = e.victim === localId;
        const pos = local ? null : this.playerPos(w, e.victim, 1.0);
        this.spawn({ priority: PRI.heal, pos, wet: 0.45, preset: SPACE.world, local, level: LVL.wound }, (t) =>
          sfx.wound(t, local),
        );
        if (local) {
          this.woundedElapsed = 0;
          this.heartbeatTimer = 0.35;
        }
        break;
      }

      case 'heal': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 1.2);
        this.spawn({ priority: PRI.heal, pos, wet: 0.45, space: 'interior', preset: SPACE.world, local, level: LVL.heal }, (t) =>
          sfx.heal(t),
        );
        break;
      }

      case 'throw': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, PLAYER_EYE_HEIGHT * 0.9);
        // A throw is a core-loop event, not a movement flourish. It gets `catch`-tier
        // priority so it can never be stolen by a footstep flurry, the `loud` rolloff so a
        // pass across the atrium is audible as intel, and its own duck so the flight that
        // follows has somewhere to live.
        this.spawn(
          { priority: PRI.catch, pos, wet: 0.42, space: 'atrium', preset: SPACE.loud, local, level: LVL.throw },
          (t) => sfx.throwRelease(t, e.charged),
        );
        this.duckBeds(e.charged ? DUCK_THROW.amount : DUCK_THROW.amount * 0.7, DUCK_THROW.seconds);
        break;
      }

      case 'catch': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, PLAYER_EYE_HEIGHT * 0.9);
        // No heal inference here. It used to guess "this catch healed you" from
        // `!chambered && !raw && healthy`, which is also exactly what a SELF-CATCH looks
        // like (`sim/gun.ts` sets chambered=false on any self-catch) and what an empty
        // catch by a healthy player looks like. So the game played a warm healing swell on
        // the two events that are supposed to feel like a disappointment. The sim emits a
        // dedicated `heal` event whenever a catch actually heals; that is the only
        // authority, and it is handled above.
        this.spawn(
          { priority: PRI.catch, pos, wet: local ? 0.2 : 0.5, space: 'interior', preset: SPACE.intel, local, level: LVL.catch },
          (t) => sfx.catchRack(t, { chambered: e.chambered, intercept: e.intercept }),
        );
        break;
      }

      case 'pickup': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 0.7);
        this.spawn({ priority: PRI.bounce, pos, wet: 0.4, preset: SPACE.world, local, level: LVL.pickup }, (t) => sfx.pickup(t));
        break;
      }

      case 'gunbounce': {
        this.spawn({ priority: PRI.bounce, pos: e.pos, wet: 0.55, space: 'atrium', preset: SPACE.intel, level: LVL.bounce }, (t) =>
          sfx.gunBounce(t, e.speed, e.surface),
        );
        break;
      }

      case 'gunreset': {
        const g = w.guns[e.gunId];
        this.spawn({ priority: PRI.bounce, pos: g ? g.pos : null, wet: 0.5, preset: SPACE.world, level: LVL.bounce }, (t) =>
          sfx.gunReset(t, e.reason),
        );
        break;
      }

      case 'pulse': {
        const local = e.player === localId;
        const connected = e.hitPlayers.length > 0 || e.hitGun >= 0;
        this.spawn({ priority: PRI.pulse, pos: e.pos, wet: 0.6, space: 'atrium', preset: SPACE.world, local, level: LVL.pulse }, (t) =>
          sfx.pulse(t, connected),
        );
        break;
      }

      case 'disarm': {
        const pos = this.playerPos(w, e.player, PLAYER_EYE_HEIGHT * 0.85);
        this.spawn(
          { priority: PRI.pulse, pos, wet: 0.55, space: 'atrium', preset: SPACE.intel, local: e.by === localId, level: LVL.disarm },
          (t) => sfx.disarm(t),
        );
        break;
      }

      case 'jump': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 0.3);
        this.spawn({ priority: PRI.jump, pos, wet: 0.25, preset: SPACE.detail, local, level: LVL.jump }, (t) =>
          sfx.jump(t, e.double),
        );
        break;
      }

      case 'land': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 0.1);
        const track = this.trackFor(e.player);
        track.surface = e.surface;
        this.spawn({ priority: PRI.land, pos, wet: 0.4, preset: SPACE.detail, local, level: LVL.land }, (t) =>
          sfx.land(t, e.speed, e.surface),
        );
        break;
      }

      case 'slidestart': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 0.25);
        this.spawn({ priority: PRI.jump, pos, wet: 0.35, preset: SPACE.detail, local, level: LVL.slide }, (t) =>
          sfx.slideStart(t),
        );
        break;
      }

      case 'dash': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 1.0);
        this.spawn({ priority: PRI.dash, pos, wet: 0.35, preset: SPACE.world, local, level: LVL.dash }, (t) => sfx.dash(t));
        break;
      }

      case 'wallrun': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 1.0);
        // Both edges. A wall-run that ended silently was the one traversal verb in the game
        // with no audio on half of its state changes.
        this.spawn(
          { priority: PRI.jump, pos, wet: 0.4, preset: SPACE.detail, local, level: e.start ? LVL.wallrun : LVL.wallrun * 0.7 },
          (t) => (e.start ? sfx.wallrunStart(t) : sfx.wallrunEnd(t)),
        );
        break;
      }

      case 'walljump': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 1.0);
        this.spawn({ priority: PRI.dash, pos, wet: 0.4, preset: SPACE.world, local, level: LVL.walljump }, (t) => sfx.wallJump(t));
        break;
      }

      case 'grapplefire': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, PLAYER_EYE_HEIGHT * 0.9);
        this.spawn({ priority: PRI.grapple, pos, wet: 0.35, preset: SPACE.world, local, level: LVL.grapple }, (t) =>
          sfx.grappleFire(t),
        );
        break;
      }

      case 'grappleattach': {
        const local = e.player === localId;
        const p = w.players[e.player];
        const pos = p?.grapplePoint ?? this.playerPos(w, e.player, 1.2);
        this.spawn({ priority: PRI.grapple, pos, wet: 0.55, space: 'atrium', preset: SPACE.world, local, level: LVL.grapple }, (t) =>
          sfx.grappleAttach(t),
        );
        break;
      }

      case 'grapplerelease': {
        const local = e.player === localId;
        const pos = this.playerPos(w, e.player, 1.2);
        this.spawn({ priority: PRI.grapple, pos, wet: 0.35, preset: SPACE.world, local, level: LVL.grapple }, (t) =>
          sfx.grappleRelease(t, e.boosted),
        );
        this.stopTether(e.player);
        break;
      }

      case 'roundstart': {
        this.spawn({ priority: PRI.stinger, pos: null, wet: 0.4, space: 'atrium', local: true, level: LVL.stinger }, (t) =>
          sfx.roundStart(t),
        );
        this.music?.setState('live');
        this.music?.setIntensity(0.25);
        break;
      }

      case 'roundend': {
        const me = w.players[localId];
        const won = me !== undefined && e.winner === me.team;
        this.spawn({ priority: PRI.stinger, pos: null, wet: 0.6, space: 'atrium', local: true, level: LVL.stinger }, (t) =>
          sfx.roundEnd(t, won, e.reason === 'draw'),
        );
        this.music?.setState('roundend');
        this.stopAllSustained();
        break;
      }

      case 'suddendeath': {
        this.spawn({ priority: PRI.stinger, pos: null, wet: 0.8, space: 'atrium', local: true, level: LVL.stinger }, (t) =>
          sfx.suddenDeath(t),
        );
        this.music?.setState('suddendeath');
        this.reverb?.setReturn('atrium', 1.15, 1.2);
        break;
      }

      case 'matchend': {
        const me = w.players[localId];
        const won = me !== undefined && e.winner === me.team;
        this.spawn({ priority: PRI.stinger, pos: null, wet: 0.7, space: 'atrium', local: true, level: LVL.stinger }, (t) =>
          sfx.matchEnd(t, won),
        );
        this.music?.setState('matchend');
        this.stopAllSustained();
        break;
      }

      case 'playerjoin': {
        if (this.uiBus) sfx.presence(this.uiBus, true);
        break;
      }

      case 'playerleave': {
        if (this.uiBus) sfx.presence(this.uiBus, false);
        this.stopScrape(e.player);
        this.stopTether(e.player);
        this.tracks.delete(e.player);
        break;
      }

      default:
        break;
    }
  }

  /**
   * UI sounds are head-locked, dry, tiny, and only ever triggered by a human clicking on
   * something — so they deliberately bypass the world voice pool rather than stealing a
   * gunshot's slot for a menu blip.
   */
  ui(kind: 'hover' | 'click' | 'back' | 'error' | 'ready' | 'countdown'): void {
    if (!this.ready || !this.sfx || !this.uiBus) return;
    this.sfx.ui(this.uiBus, kind);
  }

  /**
   * What material did the round hit?
   *
   * The audio layer has no collision world of its own, so it resolves the surface straight
   * from the arena definition: the nearest non-decorative brush or ramp to the impact point
   * owns the sound. Both the server and the client build from this same object, so the
   * answer always agrees with what actually got shot.
   */
  private surfaceAt(point: Vec3): SurfaceKind {
    let best = SurfaceKind.Concrete;
    let bestD = Number.POSITIVE_INFINITY;

    for (let i = 0; i < ARENA.brushes.length; i++) {
      const b = ARENA.brushes[i]!;
      if (b.decorative) continue;
      const d = boxDistanceSq(point, b.pos, b.half, b.yaw);
      if (d < bestD) {
        bestD = d;
        best = b.surface;
        if (d <= 0) return best;
      }
    }
    for (let i = 0; i < ARENA.ramps.length; i++) {
      const r = ARENA.ramps[i]!;
      const d = boxDistanceSq(point, r.pos, r.half, r.yaw);
      if (d < bestD) {
        bestD = d;
        best = r.surface;
      }
    }
    return best;
  }

  // -------------------------------------------------------------------------
  // Per-frame
  // -------------------------------------------------------------------------

  update(w: WorldState, localId: string, dtSec: number): void {
    if (!this.ready || !this.ctx || !this.sfx) return;
    const dt = clamp(dtSec, 0, 0.1);

    this.updateMusic(w, localId);
    this.updateAmbience(w, localId);
    this.updateHums(w, localId);
    this.updateGunFlight(w, dt);
    this.updatePlayers(w, localId, dt);
    this.updateHeartbeat(w, localId, dt);
    this.sweepVoices();
  }

  // --- Ambience ---------------------------------------------------------------

  /**
   * The world bed. Built once, on the first frame after audio comes up, and then driven
   * from the local player's speed and height for the rest of the session.
   *
   * Wind is the interesting one: it is the only continuous readout of your own velocity in
   * the game, so it doubles as speed feedback for a movement system whose whole point is
   * that you should feel fast without needing a speedometer (DESIGN.md §3).
   */
  private updateAmbience(w: WorldState, localId: string): void {
    const ctx = this.ctx;
    const sfx = this.sfx;
    if (!ctx || !sfx || !this.ambienceBus) return;

    if (!this.ambience) {
      this.ambience = sfx.ambience();
      this.ambience.output.connect(this.ambienceBus);
      this.ambience.setCity(1);
    }
    const amb = this.ambience;

    const phase = w.match.phase;
    // The atrium is quieter in menus and full during play. HIGH NOON strips the world back
    // a little so the beacons' hum owns the mix.
    const room = phase === RoundPhase.Lobby ? 0.42 : w.match.suddenDeath ? 0.6 : 0.85;
    amb.setRoom(room);
    amb.setCity(w.match.suddenDeath ? 0.45 : 1);

    const me = w.players[localId];
    if (!me || me.healthState === HealthState.Dead) {
      amb.setWind(0, 0);
      return;
    }

    const speed = Math.sqrt(me.vel.x * me.vel.x + me.vel.y * me.vel.y + me.vel.z * me.vel.z);
    const floorY = ARENA.bounds.min.y;
    const span = Math.max(ARENA.bounds.max.y - floorY, 1);
    amb.setWind(clamp01(speed / SLIDE_MAX_SPEED), clamp01((me.pos.y - floorY) / span));
  }

  private sweepVoices(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const v = this.voices[i]!;
      if (v.killed || v.endsAt <= now - 0.5) {
        this.retire(v);
        this.voices.splice(i, 1);
      }
    }
  }

  // --- Music -----------------------------------------------------------------

  private updateMusic(w: WorldState, localId: string): void {
    const music = this.music;
    if (!music) return;

    const phase = w.match.phase;
    if (phase !== this.lastPhase) {
      this.lastPhase = phase;
      const state: MusicState =
        phase === RoundPhase.Lobby
          ? 'lobby'
          : phase === RoundPhase.Warmup
            ? 'warmup'
            : phase === RoundPhase.RoundEnd
              ? 'roundend'
              : phase === RoundPhase.MatchEnd
                ? 'matchend'
                : w.match.suddenDeath
                  ? 'suddendeath'
                  : 'live';
      music.setState(state);
      if (phase !== RoundPhase.Live) this.reverb?.setReturn('atrium', 0.9, 1.5);
    } else if (phase === RoundPhase.Live && w.match.suddenDeath) {
      music.setState('suddendeath');
    }

    if (phase !== RoundPhase.Live) return;

    // Intensity: the clock, the body count, and how exposed the local player is.
    const remaining = Math.max(0, w.match.roundEndsAtMs - w.timeMs);
    const timePressure = 1 - clamp01(remaining / ROUND_TIME_MS);

    let alive = 0;
    let total = 0;
    for (const id in w.players) {
      const p = w.players[id]!;
      total++;
      if (p.healthState !== HealthState.Dead) alive++;
    }
    const aliveFrac = total > 0 ? alive / total : 1;

    const me = w.players[localId];
    let i = 0.22 + 0.42 * timePressure + 0.26 * (1 - aliveFrac);
    if (me) {
      if (me.healthState === HealthState.Wounded) i += 0.14;
      const held = me.heldGun >= 0 ? w.guns[me.heldGun] : undefined;
      if (!held || !held.chambered) i += 0.08;
      if (me.healthState === HealthState.Dead) i = Math.max(i - 0.2, 0.15);
    }
    if (w.match.suddenDeath) i = 1;
    music.setIntensity(clamp01(i));
  }

  // --- Gun hums --------------------------------------------------------------

  private updateHums(w: WorldState, localId: string): void {
    const sfx = this.sfx;
    const ctx = this.ctx;
    if (!sfx || !ctx || !this.sfxBus) return;

    const seen = new Set<number>();
    const highNoon = w.match.suddenDeath;

    for (const g of w.guns) {
      const audible = g.chambered && g.state !== GunState.Void;
      if (!audible) continue;

      const pos = this.gunWorldPos(w, g, this._pos);
      const dist = distance(pos, this.listenerPos);
      // HIGH NOON makes every hum global — nobody's position is a secret any more.
      // Out of range the hum is retired rather than left ringing at a stale position.
      if (!highNoon && dist > SPACE.intel.max) continue;
      seen.add(g.id);

      let slot = this.hums.get(g.id);
      if (!slot) {
        if (this.hums.size >= MAX_HUMS) continue;
        const voice = sfx.gunHum();
        const panner = this.makePanner(pos, SPACE.intel);
        const out = gain(ctx, 1);
        voice.output.connect(out);
        out.connect(panner);
        panner.connect(this.sfxBus);
        // A quiet, wide bed of the hum in the atrium so it fills the room a little.
        if (this.reverb) {
          const wet = gain(ctx, 0.18);
          out.connect(wet);
          wet.connect(this.reverb.send('atrium'));
        }
        slot = { voice, panner, out };
        this.hums.set(g.id, slot);
      }

      this.movePanner(slot.panner, pos, true);

      const held = g.holder !== null;
      const mine = g.holder === localId;
      let level = held ? 0.5 : 0.4;
      if (mine) level = 0.34; // your own gun sits behind everything else
      if (highNoon) {
        level = 0.85;
        slot.panner.refDistance = 400;
        slot.panner.rolloffFactor = 0;
      } else {
        slot.panner.refDistance = SPACE.intel.ref;
        slot.panner.rolloffFactor = SPACE.intel.rolloff;
      }
      slot.voice.setLevel(level);
      slot.voice.setUrgency(highNoon ? 1 : 0);
    }

    for (const [id, slot] of this.hums) {
      if (seen.has(id)) continue;
      slot.voice.stop(0.14);
      const panner = slot.panner;
      const out = slot.out;
      setTimeout(() => {
        try {
          out.disconnect();
          panner.disconnect();
        } catch {
          /* already gone */
        }
      }, 420);
      this.hums.delete(id);
    }
  }

  // --- Thrown guns: whoosh + doppler ----------------------------------------

  /**
   * The gun in the air.
   *
   * This is the update path DESIGN.md's throw hangs off, and it is the difference between a
   * throw that is one click at the moment of release and a throw that is an event you can
   * follow across an atrium with your eyes shut. Every frame a gun is airborne it gets:
   *
   *  - its panner moved to the gun's real position, so HRTF places it in the room;
   *  - a **speed** figure, driving level, brightness, tumble rate and blade pitch;
   *  - a **doppler** ratio from smoothed radial velocity, exaggerated so a pass-by is
   *    actually audible as one (see `DOPPLER_EXAGGERATION`);
   *  - a **proximity** figure, which is what makes a near miss sound like a near miss.
   *
   * The whoosh carries the `loud` rolloff, not `world`. A gun in flight is the highest-value
   * piece of information on the map — it means a team is mid-reload and both players are
   * blind — and it should be audible from across the arena for exactly that reason.
   */
  private updateGunFlight(w: WorldState, dt: number): void {
    const sfx = this.sfx;
    const ctx = this.ctx;
    if (!sfx || !ctx || !this.sfxBus) return;

    const seen = new Set<number>();
    for (const g of w.guns) {
      if (g.state !== GunState.InFlight) continue;

      const speed = Math.sqrt(g.vel.x * g.vel.x + g.vel.y * g.vel.y + g.vel.z * g.vel.z);

      // Distance and the listener→gun axis, computed once: the axis is also what the
      // doppler projection needs below, and this runs for every gun every frame.
      const dx = g.pos.x - this.listenerPos.x;
      const dy = g.pos.y - this.listenerPos.y;
      const dz = g.pos.z - this.listenerPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > SPACE.loud.max) continue;

      let slot = this.whooshes.get(g.id);
      if (!slot) {
        // Speed gates STARTING a flight, never continuing one.
        //
        // This distinction is the whole of "the whoosh must be continuous". A speed test
        // applied every frame retires the voice at the apex of any lob — a gun thrown along
        // an upward view direction genuinely passes through near-zero speed up there — and
        // then builds a fresh voice on the way down, so a single throw came out as two
        // half-whooshes with a hole punched in the middle of it. The gun's own `InFlight`
        // state is the authority on whether it is flying; speed only decides whether a
        // throw was ever brisk enough to be worth a voice. Once the voice exists it lives
        // until the gun lands or is caught, and `setSpeed` alone carries the dynamics —
        // `throwWhoosh` floors its drive at 0.22, so a slow apex goes quiet, not absent.
        if (speed < FLIGHT_MIN_SPEED) continue;
        if (this.whooshes.size >= MAX_WHOOSHES) continue;
        const voice = sfx.throwWhoosh();
        const panner = this.makePanner(g.pos, SPACE.loud);
        const out = gain(ctx, LVL.whoosh);
        voice.output.connect(out);
        out.connect(panner);
        panner.connect(this.sfxBus);
        // A little room on the flight. A whoosh with no space around it sounds like it is
        // happening in a padded box, and the arena is a concrete atrium.
        let wet: GainNode | null = null;
        if (this.reverb) {
          wet = gain(ctx, 0.3 * LVL.whoosh);
          out.connect(wet);
          wet.connect(this.reverb.send('atrium'));
        }
        slot = { voice, panner, out, wet, radial: radialOf(g.vel, dx, dy, dz, dist) };
        this.whooshes.set(g.id, slot);
      }

      seen.add(g.id);
      // A much tighter tracking constant than the default. See `movePanner`.
      this.movePanner(slot.panner, g.pos, true, 0.006);

      // --- Doppler. PannerNode dropped its own doppler support long ago, so it is by hand.
      //
      // Radial velocity comes from projecting the gun's OWN velocity vector onto the
      // listener→gun axis, not from differentiating distance frame to frame. The delta
      // method that was here divides by the frame dt, and the position it differentiates
      // arrives on 30 Hz snapshots and is then interpolated for rendering — so on a 144 Hz
      // client most frames see an unchanged position (derivative 0) followed by one frame
      // that absorbs a whole snapshot step (derivative 5x too large). What that produces is
      // not doppler, it is a square wave on the pitch, and no amount of smoothing turns it
      // back into the real thing. `g.vel` is authoritative sim state and needs no
      // differentiation at all, so the result is exact and free.
      const radialNow = radialOf(g.vel, dx, dy, dz, dist);
      // Still smoothed a little, because velocity itself steps at the snapshot rate — but
      // frame-rate independently, so the response is the same at 30 fps and at 144 fps.
      // `DOPPLER_SMOOTHING` is defined as the per-frame coefficient at 60 Hz.
      const a = 1 - Math.pow(1 - DOPPLER_SMOOTHING, clamp(dt * 60, 0.1, 4));
      slot.radial = lerpN(slot.radial, clamp(radialNow, -140, 140), a);

      const shift = slot.radial * DOPPLER_EXAGGERATION;
      const ratio = clamp(SPEED_OF_SOUND / (SPEED_OF_SOUND + clamp(shift, -260, 260)), 0.55, 1.9);
      slot.voice.setDoppler(ratio);
      slot.voice.setSpeed(clamp01(speed / FLIGHT_REF_SPEED));
      // Proximity is squared so it only really opens up for a genuine near miss rather than
      // for anything vaguely in the same half of the room.
      const near = clamp01(1 - dist / FLYBY_RADIUS);
      slot.voice.setProximity(near * near);

      // The wet/dry ratio has to track distance for the same reason it does on a one-shot:
      // that ratio IS the cue that says "far away". A static send made a pass across the
      // atrium and a pass past your ear sit in identically sized rooms, which flattens the
      // one sound in the game whose entire job is to be located.
      if (slot.wet) {
        slot.wet.gain.setTargetAtTime(this.wetFor(dist, 0.55) * LVL.whoosh, ctx.currentTime, 0.12);
      }
    }

    for (const [id, slot] of this.whooshes) {
      if (seen.has(id)) continue;
      // A longer teardown than the other sustained voices, on purpose: a catch that cuts the
      // whoosh dead in the same frame reads as an edit rather than as an object arriving.
      slot.voice.stop(0.16);
      const { panner, out, wet } = slot;
      setTimeout(() => {
        try {
          out.disconnect();
          wet?.disconnect();
          panner.disconnect();
        } catch {
          /* already gone */
        }
      }, 520);
      this.whooshes.delete(id);
    }
  }

  // --- Players: footsteps, scrapes, tethers ---------------------------------

  private trackFor(id: string): PlayerTrack {
    let t = this.tracks.get(id);
    if (!t) {
      t = { lastX: 0, lastZ: 0, strideAccum: 0, surface: SurfaceKind.Concrete };
      this.tracks.set(id, t);
    }
    return t;
  }

  private updatePlayers(w: WorldState, localId: string, dt: number): void {
    const sfx = this.sfx;
    const ctx = this.ctx;
    if (!sfx || !ctx || !this.sfxBus) return;

    const liveIds = new Set<string>();

    for (const id in w.players) {
      const p = w.players[id]!;
      liveIds.add(id);
      const track = this.trackFor(id);
      const local = id === localId;

      if (p.healthState === HealthState.Dead || !p.connected) {
        track.strideAccum = 0;
        this.stopScrape(id);
        this.stopTether(id);
        track.lastX = p.pos.x;
        track.lastZ = p.pos.z;
        continue;
      }

      const dist = local ? 0 : distance(p.pos, this.listenerPos);
      const dx = p.pos.x - track.lastX;
      const dz = p.pos.z - track.lastZ;
      const moved = Math.sqrt(dx * dx + dz * dz);
      track.lastX = p.pos.x;
      track.lastZ = p.pos.z;

      // --- Footsteps. Distance-based, so the cadence always matches the actual speed.
      if (p.grounded && p.moveState === MoveState.Grounded && moved > 0.0001 && dt > 0) {
        const speed = moved / dt;
        if (speed > 1.2) {
          track.strideAccum += moved;
          const stride = STRIDE_METRES * clamp(RUN_SPEED / Math.max(speed, 1), 0.6, 1.5) * (p.crouching ? 1.35 : 1);
          if (track.strideAccum >= stride) {
            track.strideAccum = 0;
            if (dist < SPACE.detail.max) {
              const pos = local ? null : this.playerPos(w, id, 0.1);
              this.spawn(
                { priority: PRI.footstep, pos, wet: 0.3, preset: SPACE.detail, local, level: LVL.footstep * (local ? 0.62 : 1) },
                (t) => sfx.footstep(t, track.surface, clamp01(speed / (RUN_SPEED * 1.2)), p.crouching),
              );

              // Gun-handling foley, every third step for whoever is carrying. Offset from
              // the footstep itself so it reads as a separate object moving on the body
              // rather than as part of the step, and it is also honest intel: an armed
              // player who is running is audible as one.
              const held = p.heldGun >= 0 ? w.guns[p.heldGun] : undefined;
              if (held && held.holder === id) {
                const n = (this.foleyCounters.get(id) ?? 0) + 1;
                this.foleyCounters.set(id, n);
                if (n % 3 === 0) {
                  const fPos = local ? null : this.playerPos(w, id, PLAYER_EYE_HEIGHT * 0.7);
                  this.spawn(
                    { priority: PRI.foley, pos: fPos, wet: 0.22, preset: SPACE.detail, local, level: LVL.foley * (local ? 0.7 : 1) },
                    (t) => sfx.gunFoley(t, held.chambered),
                  );
                }
              }
            }
          }
        } else {
          track.strideAccum = Math.max(0, track.strideAccum - moved);
        }
      } else if (!p.grounded) {
        track.strideAccum = STRIDE_METRES * 0.75; // land running, step immediately
      }

      // --- Sustained scrapes.
      const wantScrape: 'slide' | 'wallrun' | null =
        p.moveState === MoveState.Sliding ? 'slide' : p.moveState === MoveState.WallRunning ? 'wallrun' : null;
      if (wantScrape && dist < SUSTAIN_CULL_DIST) {
        const speed = Math.sqrt(p.vel.x * p.vel.x + p.vel.z * p.vel.z);
        let slot = this.scrapes.get(id);
        if (slot && slot.kind !== wantScrape) {
          this.stopScrape(id);
          slot = undefined;
        }
        if (!slot && this.scrapes.size < MAX_SCRAPES) {
          const voice = sfx.scrape(wantScrape);
          const panner = this.makePanner(p.pos, SPACE.detail);
          const out = gain(ctx, local ? 0.7 : 1);
          voice.output.connect(out);
          out.connect(panner);
          panner.connect(this.sfxBus);
          slot = { kind: wantScrape, voice, panner, out };
          this.scrapes.set(id, slot);
          slot.voice.setSurface(track.surface);
        }
        if (slot) {
          this._pos.x = p.pos.x;
          this._pos.y = p.pos.y + 0.3;
          this._pos.z = p.pos.z;
          this.movePanner(slot.panner, this._pos, true);
          const ref = wantScrape === 'slide' ? SLIDE_MAX_SPEED : RUN_SPEED * 1.6;
          slot.voice.setIntensity(clamp01(speed / ref));
        }
      } else {
        this.stopScrape(id);
      }

      // --- Grapple tether.
      if (p.grappleAnchorId !== GRAPPLE_NONE && p.grapplePoint && dist < SUSTAIN_CULL_DIST) {
        let slot = this.tethers.get(id);
        if (!slot && this.tethers.size < MAX_TETHERS) {
          const voice = sfx.tether();
          const panner = this.makePanner(p.pos, SPACE.world);
          const out = gain(ctx, local ? 0.85 : 0.7);
          voice.output.connect(out);
          out.connect(panner);
          panner.connect(this.sfxBus);
          slot = { voice, panner, out };
          this.tethers.set(id, slot);
        }
        if (slot) {
          this._pos.x = p.pos.x;
          this._pos.y = p.pos.y + 1.2;
          this._pos.z = p.pos.z;
          this.movePanner(slot.panner, this._pos, true);
          // Tension: how taut the tether is, plus how fast you are swinging through it.
          const len = distance(p.pos, p.grapplePoint);
          const taut = clamp01(len / Math.max(p.grappleLength, 0.001));
          const speed = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y + p.vel.z * p.vel.z);
          slot.voice.setTension(clamp01(taut * 0.55 + (speed / GRAPPLE_MAX_SPEED) * 0.65));
        }
      } else {
        this.stopTether(id);
      }
    }

    // Retire anything belonging to a player who is no longer in the world.
    for (const id of Array.from(this.scrapes.keys())) if (!liveIds.has(id)) this.stopScrape(id);
    for (const id of Array.from(this.tethers.keys())) if (!liveIds.has(id)) this.stopTether(id);
    for (const id of Array.from(this.tracks.keys())) if (!liveIds.has(id)) this.tracks.delete(id);
    for (const id of Array.from(this.foleyCounters.keys())) if (!liveIds.has(id)) this.foleyCounters.delete(id);
  }

  private stopScrape(id: string): void {
    const slot = this.scrapes.get(id);
    if (!slot) return;
    this.scrapes.delete(id);
    slot.voice.stop(0.1);
    const { panner, out } = slot;
    setTimeout(() => {
      try {
        out.disconnect();
        panner.disconnect();
      } catch {
        /* already gone */
      }
    }, 380);
  }

  private stopTether(id: string): void {
    const slot = this.tethers.get(id);
    if (!slot) return;
    this.tethers.delete(id);
    slot.voice.stop(0.09);
    const { panner, out } = slot;
    setTimeout(() => {
      try {
        out.disconnect();
        panner.disconnect();
      } catch {
        /* already gone */
      }
    }, 360);
  }

  private stopAllSustained(): void {
    for (const id of Array.from(this.scrapes.keys())) this.stopScrape(id);
    for (const id of Array.from(this.tethers.keys())) this.stopTether(id);
    for (const [id, slot] of this.whooshes) {
      slot.voice.stop(0.08);
      const { panner, out, wet } = slot;
      setTimeout(() => {
        try {
          out.disconnect();
          wet?.disconnect();
          panner.disconnect();
        } catch {
          /* already gone */
        }
      }, 340);
      this.whooshes.delete(id);
    }
  }

  // --- Wound heartbeat -------------------------------------------------------

  /**
   * The wound heartbeat. It quickens the longer you stay wounded, and it quickens further
   * when an enemy is holding a chambered gun near you — the sound is telling you something
   * true about the situation, which is the only reason to have it at all.
   */
  private updateHeartbeat(w: WorldState, localId: string, dt: number): void {
    const sfx = this.sfx;
    if (!sfx) return;
    const me = w.players[localId];
    if (!me || me.healthState !== HealthState.Wounded || w.match.phase !== RoundPhase.Live) {
      this.woundedElapsed = 0;
      this.heartbeatTimer = 0;
      return;
    }

    this.woundedElapsed += dt;

    let threat = 0;
    for (const g of w.guns) {
      if (!g.chambered || !g.holder || g.holder === localId) continue;
      const holder = w.players[g.holder];
      if (!holder || holder.team === me.team) continue;
      const d = distance(holder.pos, me.pos);
      threat = Math.max(threat, clamp01(1 - d / 30));
    }

    const settle = clamp01(this.woundedElapsed / 15);
    const intensity = clamp01(settle * 0.6 + threat * 0.7);
    const interval = lerpN(0.95, 0.5, intensity);

    this.heartbeatTimer -= dt;
    if (this.heartbeatTimer <= 0) {
      this.heartbeatTimer += interval;
      if (this.heartbeatTimer <= 0) this.heartbeatTimer = interval;
      this.spawn({ priority: PRI.heal, pos: null, wet: 0, local: true, level: LVL.heartbeat }, (t) =>
        sfx.heartbeat(t, intensity),
      );
    }
  }
}

/**
 * Radial velocity of a source, in m/s, positive when it is moving AWAY from the listener.
 *
 * `dx/dy/dz` is the listener→source vector and `dist` its length, both of which the caller
 * has already computed. Projecting the source's own velocity onto that axis is the exact
 * definition of the quantity doppler needs, and unlike differentiating distance per frame it
 * has no dependence on the frame rate, the snapshot rate or the interpolator.
 */
function radialOf(vel: Vec3, dx: number, dy: number, dz: number, dist: number): number {
  if (dist < 0.001) return 0;
  return (vel.x * dx + vel.y * dy + vel.z * dz) / dist;
}

/** Squared distance from a point to a yaw-rotated box. Zero when the point is inside. */
function boxDistanceSq(point: Vec3, center: Vec3, half: Vec3, yaw: number): number {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const dz = point.z - center.z;

  let lx = dx;
  let lz = dz;
  if (yaw !== 0) {
    const c = Math.cos(-yaw);
    const s = Math.sin(-yaw);
    lx = dx * c - dz * s;
    lz = dx * s + dz * c;
  }

  const ox = Math.max(Math.abs(lx) - half.x, 0);
  const oy = Math.max(Math.abs(dy) - half.y, 0);
  const oz = Math.max(Math.abs(lz) - half.z, 0);
  return ox * ox + oy * oy + oz * oz;
}

/** Convenience for callers that want a rough loudness read on a player, e.g. for HUD meters. */
export function playerSpeed01(p: PlayerState): number {
  const s = Math.sqrt(p.vel.x * p.vel.x + p.vel.z * p.vel.z);
  return clamp01(s / SLIDE_MAX_SPEED);
}
